/** The tenant field of a create dialog that the page can pre-select (D9).
 *
 * - `pageTenants` NOT passed (`undefined`): the legacy behaviour, unchanged —
 *   `useScopedTenantState()`, which follows the console scope until the user
 *   picks something here.
 * - `pageTenants` passed: plain state, seeded on every open with the page's
 *   operator when the page names exactly one, otherwise empty. The page already
 *   folded the scope into what it passes (URL > preset > scope), so the scope
 *   never refills this field: a page cleared to `?tenant=all` under a scope
 *   passes `[]` and the field opens empty.
 *
 * `reset()` goes back to the default — the page's seed, or the scope in the
 * legacy mode. A SEMANTIC clear ("System tag — no tenant") stays
 * `setTenant(null)` in both modes; see the "reset() is not clear()" lesson.
 *
 * Seeding in an `open` effect, not in `useState`, matters: most dialogs stay
 * mounted with an `open` prop, so a mount-time seed would be stale by the next
 * open (and a Cancelled pick would survive it).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

import { singlePageTenant } from './pageTenants';
import { useScopedTenantState, type UseScopedTenantState } from './useScopedTenantState';

export function useCreateDialogTenant(
  open: boolean,
  pageTenants: TenantPickerValue[] | undefined,
): UseScopedTenantState {
  const scoped = useScopedTenantState();
  const [own, setOwn] = useState<TenantPickerValue | null>(() => singlePageTenant(pageTenants));

  // Read at open / reset time, so a re-render with a fresh array never re-seeds
  // a field the user is editing.
  const pageRef = useRef(pageTenants);
  pageRef.current = pageTenants;

  useEffect(() => {
    if (open && pageRef.current !== undefined) setOwn(singlePageTenant(pageRef.current));
  }, [open]);

  const reset = useCallback(() => setOwn(singlePageTenant(pageRef.current)), []);

  return pageTenants === undefined ? scoped : [own, setOwn, reset];
}
